import {ReactElement, createContext, useEffect, useMemo, useState} from "react";
import {useLocation} from "react-router";
import {useLocalStorage} from "react-use";

import {TabProps} from "../../components/input";
import {Component} from "../../types";
import {BadgeComponent} from "../../components/badge";
import {DropdownButton, TabsSelectSomeMenu} from "../../components/input/dropdown-button";
import {NavItem, Page} from "../../layout";
import {Span2} from "../../components/text";
import {
	CollectionStatus,
	ShareStatus,
	Collection,
	ShareEvent,
	useGroupList,
	useUserList,
	useMyUser,
} from "../../data";
import {useCompanyPagesList} from "../../data/company";

import styles from "./collection.module.scss";

interface CollectionsPageProps extends Component {
	tabs?: NavItem[];
}

export const statusBadges: Record<string, BadgeComponent> = {
	draft: {
		color: "black",
		text: "Draft",
	},
	scheduled: {
		color: "black",
		text: "Scheduled",
	},
	delivered: {
		color: "blue",
		text: "Delivered",
	},
	failed: {
		color: "pink",
		text: "Failed To Share",
	},
};

export const getCollectionStatusBadge = (item: Collection, saved?: "saved" | "saving"): BadgeComponent => {
	const badge = item.sent
		? statusBadges.delivered
		: statusBadges[item.status === "approved" ? "scheduled" : item.status];
	if (saved) return {...badge, text: `${badge.text} - ${saved.uFirst()}`};
	return badge;
};

export const getShareStatusBadge = (item: ShareEvent, saved?: "saved" | "saving"): BadgeComponent => {
	const badge =
		item.sharedAt && !item.result
			? statusBadges.failed
			: item.sharedAt && item.result
			? statusBadges.delivered
			: item.scheduledFor
			? statusBadges.scheduled
			: statusBadges.draft;

	if (saved) return {...badge, text: `${badge.text} - ${saved.uFirst()}`};
	return badge;
};

interface CollectionFilter {
	status: CollectionStatus[] | ShareStatus[];
	groups: number[];
	owner: string[];
	sort: "created" | "scheduled";
	accounts: number[];
}
interface CollectionTab {
	personalShareEvents: boolean;
	setPersonalShareEvents: (value: boolean) => void;
}

interface CollectionContextProps {
	filter: CollectionFilter;
	tab: CollectionTab;
}

export const CollectionContext = createContext<CollectionContextProps>({
	filter: {
		status: [],
		owner: [],
		groups: [],
		sort: "created",
		accounts: [],
	},
	tab: {
		personalShareEvents: false,
		setPersonalShareEvents: () => undefined,
	},
});

const defaultFilter = (): CollectionFilter => ({
	status: [],
	owner: [],
	groups: [],
	sort: "created",
	accounts: [],
});

export const CollectionPage = ({tabs}: CollectionsPageProps): ReactElement => {
	const [filter, setFilter] = useState(defaultFilter);
	const [cookieValue, setPersonalShareEvents] = useLocalStorage("collections-posts-personal", false);
	const personalShareEvents = useMemo(() => cookieValue ?? false, [cookieValue]);
	const me = useMyUser();
	const location = useLocation();
	const groups = useGroupList();
	const users = useUserList();
	const companies = useCompanyPagesList();

	useEffect(() => setFilter(defaultFilter), [location]);

	const title = location.pathname.includes("posts") ? "Posts" : "Collections";
	const total = useMemo(
		() =>
			Object.keys(filter).reduce(
				(acc, current) =>
					acc +
					(Array.isArray(filter[current]) && (current !== "accounts" || !personalShareEvents)
						? filter[current].length
						: 0),
				0
			),
		[filter, personalShareEvents]
	);

	const filterLabel = total > 0 ? `${total} filter(s) applied` : "Filter By";
	const postsTab = useMemo(() => {
		let t: TabProps<keyof Omit<CollectionFilter, "sort">, string | number>[] = [
			{
				label: "Status",
				value: "status",
				options: [
					{value: "scheduled", label: "Scheduled"},
					{value: "delivered", label: "Delivered"},
				],
			},
		];
		if (!personalShareEvents) {
			t = [{label: "Account", value: "accounts", options: companies}, ...t];
		}
		return t;
	}, [companies, personalShareEvents]);

	const filterByButton: TabProps<keyof Omit<CollectionFilter, "sort">, string | number>[] = useMemo(
		() =>
			title === "Collections"
				? [
						{
							label: "Status",
							value: "status",
							options: [
								{value: "draft", label: "Draft"},
								{value: "approved", label: "Scheduled"},
								{value: "sent", label: "Delivered"},
							],
						},
						{label: "Creator", value: "owner", options: users},
						{label: "Recipients", value: "groups", options: groups},
				  ]
				: me.role === "admin"
				? postsTab
				: [
						{
							label: "Status",
							value: "status",
							options: [
								{value: "scheduled", label: "Scheduled"},
								{value: "delivered", label: "Delivered"},
							],
						},
				  ],
		[groups, title, users, me, postsTab]
	);

	return (
		<CollectionContext.Provider
			value={{
				filter,
				tab: {
					personalShareEvents,
					setPersonalShareEvents,
				},
			}}
		>
			<Page
				title="Builder"
				parentPath="/collections"
				tabs={tabs}
				input={
					<>
						<Span2 className={styles.showAll} color="grey">
							{total > 0 ? "" : `Upcoming ${title}`}
						</Span2>
						<DropdownButton
							value="Sort By"
							arrow
							invert
							options={[
								{label: "Last Created", onClick: () => setFilter(c => ({...c, sort: "created"}))},
								{label: "Scheduled", onClick: () => setFilter(c => ({...c, sort: "scheduled"}))},
							]}
						/>
						<TabsSelectSomeMenu
							label={filterLabel}
							value={filter}
							tabs={filterByButton}
							onChange={v => setFilter(c => ({...c, ...v} as CollectionFilter))}
						/>
					</>
				}
			/>
		</CollectionContext.Provider>
	);
};

export {Collections} from "./collections";
export {CollectionItem} from "./collection-item";
export {EditCollection} from "./edit";
export {EditShare} from "./share-editor";
export {Posts} from "./posts";
